import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ITab } from 'src/app/interfaces/tab';
import { ICta } from 'src/app/interfaces/cta';
import { IKeyboardShortcut } from 'src/app/interfaces/keyboard-shortcut';
import { ISegment } from 'src/app/interfaces/segment.interface';
import { IUtility, IUtilityChange } from 'src/app/interfaces/utility';
import { IWidgetCtaTrackingItem } from 'src/app/interfaces/widget-cta-tracking.interface';
import { IActiveFilterState, ISortConfig, INestedEntityContext } from 'src/app/interfaces/nested-entity';

export type ActionsPaletteDisplayMode = 'modal' | 'overlay' | 'pinned';

export interface IActionsPaletteLauncherConfig {
  label?: string;
  icon?: string;
  tooltip?: string;
  shortcutLabel?: string;
  shortcutCode?: string;
  shortcutKeyLabel?: string;
  shortcutDescription?: string;
  configs?: Record<string, any>;
}

// Minimum screen width to allow non-modal display modes
const MIN_WIDTH_FOR_ALTERNATE_MODES = 1200;

@Injectable({
  providedIn: 'root'
})
export class ActionsPaletteService {
  private isOpenSubject = new Subject<boolean>();
  public isOpen$ = this.isOpenSubject.asObservable();
  private _isOpen = false;

  // Config change notification - emits when configs are updated while palette is already open
  private configChangedSubject = new Subject<void>();
  public configChanged$ = this.configChangedSubject.asObservable();

  // Display mode state - no longer persisted to localStorage
  private displayModeSubject!: BehaviorSubject<ActionsPaletteDisplayMode>;
  public displayMode$!: Observable<ActionsPaletteDisplayMode>;
  private isBrowser: boolean = false;

  // Dynamic data from current layout
  private currentTabsSubject = new BehaviorSubject<ITab[]>([]);
  public currentTabs$ = this.currentTabsSubject.asObservable();
  
  private currentCtasSubject = new BehaviorSubject<ICta[]>([]);
  public currentCtas$ = this.currentCtasSubject.asObservable();

  private currentKeyboardShortcutsSubject = new BehaviorSubject<IKeyboardShortcut[]>([]);
  public currentKeyboardShortcuts$ = this.currentKeyboardShortcutsSubject.asObservable();

  private currentSegmentsSubject = new BehaviorSubject<ISegment[]>([]);
  public currentSegments$ = this.currentSegmentsSubject.asObservable();
  
  private currentUtilitiesSubject = new BehaviorSubject<IUtility[]>([]);
  public currentUtilities$ = this.currentUtilitiesSubject.asObservable();

  private contextualLauncherSubject = new BehaviorSubject<IActionsPaletteLauncherConfig | null>(null);
  public contextualLauncher$ = this.contextualLauncherSubject.asObservable();

  // Widget CTA tracking
  private widgetCtasSubject = new BehaviorSubject<IWidgetCtaTrackingItem[]>([]);
  public widgetCtas$ = this.widgetCtasSubject.asObservable();

  // CTA emitter callback
  private ctaEmitterCallback?: (cta: ICta) => void;
  // Keyboard shortcut emitter callback  
  private keyboardShortcutEmitterCallback?: (shortcut: IKeyboardShortcut) => void;
  // Utility change emitter callback
  private utilityChangeEmitterCallback?: (change: IUtilityChange) => void;

  // Route tracking for widget CTA cleanup
  private currentRoute: string = '';
  private currentQueryParams: string = '';

  // Filter and sort state from data components
  private inheritedFilterState: IActiveFilterState | null = null;
  private inheritedSortConfig: ISortConfig | null = null;
  private inheritedEntityContext: INestedEntityContext | null = null;


  // Default configuration for actions palette
  private defaultActionsPaletteConfigs = {
    autoSelectMode: null, // 'business' | 'partner' | 'funnel' | 'tool' | null
    hideModeSelector: false,
    hideFilterByTypeToolTip: false,
    autoSelectAll: false,
    showHeader: false,
    headerHeading: '',
    headerDescription: '',
    showAddButton: false,
    hideResults: false,
    preSelectedToTabId: null, // string - ID of tab to pre-select (e.g., 'business-tasks')
    hideShowCreateHint: false, // Hide the "Press Shift+Enter to add" hint
    preSelectCreateSegment: null, // string - Pre-select a segment for business creation (e.g., 'lead', 'prospect', etc.)
    hideWildcardHint: false, // Hide the "Press * to load all" hint in partner/funnel modes
    autoSearchQuery: null, // string - Automatically add text to search query after mode is set (e.g., 'add task')
    autoOpenCrudMode: false, // boolean - Automatically open CRUD mode for creating nested entities
    crudModeDefaultTarget: null, // string - Entity type ID to create (e.g., 'tasks', 'notes', 'pages')
    crudModeDefaultParent: null, // string - Parent entity ID to pre-select (e.g., business UID, partner UID, funnel ID)
    crudModeDefaultParentType: null, // 'business' | 'partner' | 'funnel' - Explicitly set the parent type
    crudModeDefaultProjectId: null, // string - Project ID to associate with the created entity
    crudModeTemplateStepId: null, // string - Template step ID to link the created entity to (for project workflow templates)
    crudModeInitialName: null, // string - Initial name for create mode
    crudModeInitialValues: null, // object - Initial field values for create mode
    linkCreatedTaskToTicketId: null, // string - Ticket ID to update when a task is created
    linkCreatedTaskReturnToTicket: false, // boolean - Include ticketId on task navigation after create
    crudModeEntity: null, // object - Existing entity to edit in CRUD mode (if provided, opens in edit mode instead of create)
    autoOpenIndexMode: true, // boolean - Automatically open Index mode for viewing nested entities (default to agency view)
    indexModeDefaultTarget: null, // string - Entity type ID to view (null = show parent info view with no entity selected)
    indexModeDefaultParent: '__CURRENT_AGENCY__', // string - Special value that will be replaced with current agency ID at runtime
    indexModeDefaultParentType: 'agency', // 'business' | 'partner' | 'funnel' | 'agency' - Default to agency parent type
    hideIndexModeSelector: false, // boolean - Hide the entity type chips in index mode
    hideViewAllInIndex: false, // boolean - Hide the "View All" result at the top of index mode
    replaceRouteOnNavigation: false, // boolean - Use replaceUrl: true when navigating to replace current route in history instead of adding to it
    limit: null, // number | null - Limit the number of entities loaded in index mode (null = no limit)
    preferredDisplayMode: null as ActionsPaletteDisplayMode | null // 'modal' | 'overlay' | 'pinned' | null - Preferred display mode (only applies on wide screens >1200px)
  };

  // variables to pass on launch
  actionsPaletteConfigs = { ...this.defaultActionsPaletteConfigs };

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    
    // Initialize display mode - always starts as 'modal', no persistence
    this.displayModeSubject = new BehaviorSubject<ActionsPaletteDisplayMode>('modal');
    this.displayMode$ = this.displayModeSubject.asObservable();
    
    this.initializeRouteWatcher();
  }

  // Display mode management - no persistence
  get displayMode(): ActionsPaletteDisplayMode {
    return this.displayModeSubject.value;
  }

  /**
   * Set display mode (e.g., from keyboard shortcut)
   * Does NOT persist - resets to 'modal' on page refresh
   */
  setDisplayMode(mode: ActionsPaletteDisplayMode): void {
    // Only allow non-modal modes on wide screens
    if (mode !== 'modal' && !this.isWideScreen()) {
      return; // Silently ignore - stay in modal mode on narrow screens
    }
    this.displayModeSubject.next(mode);
  }

  /**
   * Check if screen is wide enough for alternate display modes
   */
  private isWideScreen(): boolean {
    if (!this.isBrowser) return false;
    return window.innerWidth >= MIN_WIDTH_FOR_ALTERNATE_MODES;
  }

  /**
   * Get the effective display mode, considering preferred mode and screen width
   */
  getEffectiveDisplayMode(preferredMode?: ActionsPaletteDisplayMode): ActionsPaletteDisplayMode {
    // If screen is narrow, always use modal
    if (!this.isWideScreen()) {
      return 'modal';
    }
    
    // If a preferred mode is specified, use it (for wide screens)
    if (preferredMode) {
      return preferredMode;
    }
    
    // Otherwise use the current display mode
    return this.displayModeSubject.value;
  }

  openWithConfigs(configs?: any): void {
    // Check for preferred display mode in configs
    if (configs?.preferredDisplayMode) {
      const effectiveMode = this.getEffectiveDisplayMode(configs.preferredDisplayMode);
      this.displayModeSubject.next(effectiveMode);
    }
    
    this.actionsPaletteConfigs = { ...this.actionsPaletteConfigs, ...configs };
    
    // If already open, notify that configs changed so component can refresh
    if (this._isOpen) {
      this.configChangedSubject.next();
    } else {
      this.open();
    }
  }

  /**
   * Open the actions palette with filter and sort state from a data component
   * This creates a seamless workflow from data tables to actions palette
   */
  openWithDataContext(options: {
    entityContext: INestedEntityContext;
    filterState?: IActiveFilterState;
    sortConfig?: ISortConfig;
    limit?: number; // Optional limit from data component
    configs?: any;
  }): void {
    // Store the inherited state
    this.inheritedEntityContext = options.entityContext;
    this.inheritedFilterState = options.filterState || null;
    this.inheritedSortConfig = options.sortConfig || null;

    // Set configs for automatic index mode
    const defaultConfigs = {
      autoOpenIndexMode: true,
      indexModeDefaultTarget: options.entityContext.entityConfig?.entityType,
      indexModeDefaultParent: options.entityContext.parentId,
      indexModeDefaultParentType: options.entityContext.parentType,
      hideIndexModeSelector: true, // Simplified UI when coming from data component
      showHeader: true,
      headerHeading: `${options.entityContext.entityConfig?.displayName || 'Items'}`,
      headerDescription: `Filtered view from data table`,
      limit: options.limit || null // Pass through the limit from data component
    };

    // Merge with any additional configs
    const finalConfigs = { ...defaultConfigs, ...options.configs };
    
    console.log('Opening Actions Palette with data context:', {
      entityContext: options.entityContext,
      filterState: options.filterState,
      sortConfig: options.sortConfig,
      configs: finalConfigs
    });

    this.openWithConfigs(finalConfigs);
  }

  private initializeRouteWatcher(): void {
    // Set initial route from current router state
    const currentUrl = this.router.url;
    this.currentRoute = currentUrl.split('?')[0];
    this.currentQueryParams = currentUrl.split('?')[1] || '';

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(event => {
        const navigationEvent = event as NavigationEnd;
        const newRoute = navigationEvent.urlAfterRedirects.split('?')[0];
        const newQueryParams = navigationEvent.urlAfterRedirects.split('?')[1] || '';
        
        // Clear widget CTAs if route or query params changed
        if (this.currentRoute !== newRoute || this.currentQueryParams !== newQueryParams) {
          this.clearWidgetCtas();

          if (this.currentRoute !== newRoute) {
            this.clearContextualLauncher();
          }

          this.currentRoute = newRoute;
          this.currentQueryParams = newQueryParams;
        }
      });
  }

  setContextualLauncher(config: IActionsPaletteLauncherConfig | null): void {
    this.contextualLauncherSubject.next(config);
  }

  clearContextualLauncher(): void {
    if (this.contextualLauncherSubject.value !== null) {
      this.contextualLauncherSubject.next(null);
    }
  }

  getCurrentContextualLauncher(): IActionsPaletteLauncherConfig | null {
    return this.contextualLauncherSubject.value;
  }

  openFromContextualLauncher(): void {
    const launcher = this.contextualLauncherSubject.value;

    if (launcher?.configs) {
      this.openWithConfigs(launcher.configs);
      return;
    }

    this.open();
  }

  toggleFromContextualLauncher(): void {
    if (this.isOpen()) {
      this.close();
      return;
    }

    this.openFromContextualLauncher();
  }

  open(): void {
    if (!this._isOpen) {
      this._isOpen = true;
      this.isOpenSubject.next(true);
    }
  }

  close(): void {
    if (this._isOpen) {
      this._isOpen = false;
      this.isOpenSubject.next(false);
      // Reset configs to default when closing
      this.resetConfigs();
      // Reset display mode to modal (no persistence)
      this.displayModeSubject.next('modal');
    }
  }

  // Reset actionsPaletteConfigs to default values
  private resetConfigs(): void {
    this.actionsPaletteConfigs = { ...this.defaultActionsPaletteConfigs };
    // Clear inherited state when resetting
    this.clearInheritedState();
  }

  /**
   * Get inherited filter state from data component
   */
  getInheritedFilterState(): IActiveFilterState | null {
    return this.inheritedFilterState;
  }

  /**
   * Get inherited sort config from data component  
   */
  getInheritedSortConfig(): ISortConfig | null {
    return this.inheritedSortConfig;
  }

  /**
   * Get inherited entity context from data component
   */
  getInheritedEntityContext(): INestedEntityContext | null {
    return this.inheritedEntityContext;
  }

  /**
   * Clear inherited state (called when actions palette closes)
   */
  private clearInheritedState(): void {
    this.inheritedFilterState = null;
    this.inheritedSortConfig = null;
    this.inheritedEntityContext = null;
  }

  /**
   * Check if actions palette was opened with inherited data context
   */
  hasInheritedContext(): boolean {
    return this.inheritedEntityContext !== null;
  }

  isOpen(): boolean {
    return this._isOpen;
  }

  // Update current layout data
  // Note: keyboardShortcuts from layout are merged with existing shortcuts set via updateKeyboardShortcuts
  // This allows child components to register their own shortcuts without being overwritten by layout updates
  updateLayoutData(tabs: ITab[], ctas: ICta[], keyboardShortcuts: IKeyboardShortcut[] = [], segments: ISegment[] = [], utilities: IUtility[] = []): void {
    this.currentTabsSubject.next(tabs);
    this.currentCtasSubject.next(ctas);
    
    // Merge layout shortcuts with existing component-specific shortcuts
    // Layout shortcuts are those passed via @Input, component shortcuts are set via updateKeyboardShortcuts
    if (keyboardShortcuts.length > 0) {
      // If layout provides shortcuts, merge them with existing (component shortcuts take precedence)
      const existingShortcuts = this.currentKeyboardShortcutsSubject.value;
      const existingCodes = new Set(existingShortcuts.map(s => s.code));
      const newLayoutShortcuts = keyboardShortcuts.filter(s => !existingCodes.has(s.code));
      this.currentKeyboardShortcutsSubject.next([...existingShortcuts, ...newLayoutShortcuts]);
    }
    // If keyboardShortcuts is empty, preserve existing shortcuts (don't overwrite component shortcuts)
    
    this.currentSegmentsSubject.next(segments);
    this.currentUtilitiesSubject.next(utilities);
  }

  getCurrentTabs(): ITab[] {
    return this.currentTabsSubject.value;
  }

  getCurrentCtas(): ICta[] {
    return this.currentCtasSubject.value;
  }

  getCurrentKeyboardShortcuts(): IKeyboardShortcut[] {
    return this.currentKeyboardShortcutsSubject.value;
  }

  getCurrentSegments(): ISegment[] {
    return this.currentSegmentsSubject.value;
  }

  getCurrentUtilities(): IUtility[] {
    return this.currentUtilitiesSubject.value;
  }

  getCurrentWidgetCtas(): IWidgetCtaTrackingItem[] {
    return this.widgetCtasSubject.value;
  }

  // Set CTA emitter callback
  setCtaEmitter(callback: (cta: ICta) => void): void {
    this.ctaEmitterCallback = callback;
  }

  // Set keyboard shortcut emitter callback
  setKeyboardShortcutEmitter(callback: (shortcut: IKeyboardShortcut) => void): void {
    this.keyboardShortcutEmitterCallback = callback;
  }

  // Set utility change emitter callback
  setUtilityChangeEmitter(callback: (change: IUtilityChange) => void): void {
    this.utilityChangeEmitterCallback = callback;
  }

  // Emit CTA event
  emitCta(cta: ICta): void {
    if (this.ctaEmitterCallback) {
      this.ctaEmitterCallback(cta);
    }
  }

  // Emit keyboard shortcut event
  emitKeyboardShortcut(shortcut: IKeyboardShortcut): void {
    if (this.keyboardShortcutEmitterCallback) {
      this.keyboardShortcutEmitterCallback(shortcut);
    }
  }

  // Emit utility change event
  emitUtilityChange(change: IUtilityChange): void {
    if (this.utilityChangeEmitterCallback) {
      this.utilityChangeEmitterCallback(change);
    }
  }

  // Update keyboard shortcuts for the actions palette
  // This method replaces all shortcuts - used by components to set their specific shortcuts
  // When a component is destroyed, it should call this with [] to clear its shortcuts
  updateKeyboardShortcuts(shortcuts: IKeyboardShortcut[]): void {
    this.currentKeyboardShortcutsSubject.next(shortcuts);
  }

  // Widget CTA management methods
  addWidgetCtas(widgetId: string, widget: any, ctas: any[], sourceType: 'widget' | 'dashboard' | 'other' = 'widget', sourceLabel?: string): void {
    const currentWidgetCtas = this.widgetCtasSubject.value;
    
    // Remove existing CTAs for this widget
    const filteredCtas = currentWidgetCtas.filter(item => item.widgetId !== widgetId);
    
    // Add new CTAs for this widget if any exist
    if (ctas && ctas.length > 0) {
      const newTrackingItems: IWidgetCtaTrackingItem[] = ctas.map(cta => {
        // Use description as fallback when label is empty (for icon-only buttons)
        const displayLabel = cta.label && cta.label.trim() 
          ? cta.label 
          : cta.description || `Action: ${cta.id}`;
        
        return {
          widget: widget,
          cta: {
            ...cta,
            label: displayLabel // Ensure Actions Palette always has a label
          },
          widgetId: widgetId,
          sourceType: sourceType,
          sourceLabel: sourceLabel
        };
      });
      
      filteredCtas.push(...newTrackingItems);
    }
    
    this.widgetCtasSubject.next(filteredCtas);
  }

  clearWidgetCtas(): void {
    this.widgetCtasSubject.next([]);
  }

  removeWidgetCtas(widgetId: string): void {
    const currentWidgetCtas = this.widgetCtasSubject.value;
    const filteredCtas = currentWidgetCtas.filter(item => item.widgetId !== widgetId);
    this.widgetCtasSubject.next(filteredCtas);
  }
}
